import { forwardRef } from 'react';
import { Disableable, Sized } from '@optimus-web/common';
import { UnicodeEntity } from '@mews/enums';
import { useId } from '@optimus-web/helpers';
import { VisuallyHidden } from '../helpers';
import { TextareaContainerElement, TextareaPlaceholder, TextareaStyledComponent, ValueContainerElement } from './style';

export interface TextareaElementProps extends Sized, Disableable {
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>, value?: string) => void;
    onHeightChange?: (height: string) => void;
    placeholder?: React.ReactNode;
    rows?: number;
    children?: React.ReactNode;
    form?: string;
    label?: React.ReactNode;
    value?: string;
    name: string;
    id?: string;
    required?: boolean;
    error?: React.ReactNode;
    inline?: boolean;
    ariaLabel?: string;
    autoComplete?: HTMLTextAreaElement['autocomplete'];
    helper?: React.ReactNode;
}

export const TextareaElement = forwardRef(
    (
        {
            name,
            id,
            value = '',
            placeholder,
            children,
            rows,
            onChange,
            size,
            disabled,
            required,
            error,
            inline,
            ariaLabel,
            helper,
            label,
            ...other
        }: TextareaElementProps,
        ref: React.Ref<HTMLTextAreaElement>
    ) => {
        const combinedLabelId = useId('textarea-combined-label');
        const shouldCreateCombinedLabel = label && ariaLabel;

        const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (onChange) {
                onChange(event, event.target.value);
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            event.stopPropagation();
        };

        const textareaProps = {
            ...other,
            ref,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            value,
            rows,
            inputSize: size,
            disabled,
            id: name,
            required,
            error,
            name,
            helper,
            'aria-invalid': !!error,
            'aria-labelledby': shouldCreateCombinedLabel ? combinedLabelId : undefined,
            'aria-label': !label && ariaLabel ? ariaLabel : undefined,
            'aria-required': required,
            'aria-describedby':
                [helper && `${name}-helper`, error && `${name}-error`].filter(Boolean).join(' ') || undefined,
        };

        return (
            <TextareaContainerElement>
                <ValueContainerElement>
                    {!value && (
                        <TextareaPlaceholder inputSize={size} disabled={disabled}>
                            {placeholder}
                        </TextareaPlaceholder>
                    )}
                    <TextareaStyledComponent {...textareaProps} />
                    {children}
                </ValueContainerElement>
                {shouldCreateCombinedLabel ? (
                    <VisuallyHidden id={combinedLabelId} aria-hidden="true">
                        {label}
                        {UnicodeEntity.Comma}
                        {UnicodeEntity.Space}
                        {ariaLabel}
                    </VisuallyHidden>
                ) : null}
            </TextareaContainerElement>
        );
    }
);
